import { Component, OnDestroy, OnInit, isDevMode } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout'
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms'
import { HttpClient, HttpResponse, HttpStatusCode } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { ExitConfirmationDialogComponent } from '../dialogs/exit-confirmation-dialog/exit-confirmation-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { CanExit } from '../services/authentication.service'
import { MatSnackBar } from '@angular/material/snack-bar'
import { CallerService } from '../services/caller.service'
import { initializePostsHistory } from '../ngrx-store/actions/corp-member.actions'
import { AccommodationType, AppState, PostHistoryState, SaleType } from '../ngrx-store/app.state'
import { Store } from '@ngrx/store'

@Component({
    selector: 'app-agent-dashboard',
    templateUrl: './agent-dashboard.component.html',
    styleUrls: ['./agent-dashboard.component.css'],
})
export class AgentDashboardComponent implements OnInit, OnDestroy, CanExit {

    /* All user's bookmarked items */
    allBookmarks: any[] = []

    /* All user's items they've posted */
    allPosts: PostHistoryState = new Map()

    get savedSales(): SaleType[] {
        return this.allBookmarks.filter((item) => item?._type === 'sale')
    }

    get savedAccommodations(): AccommodationType[] {
        return this.allBookmarks.filter((item) => item?._type === 'accommodation')
    }

    /** Sold ones included - the seller sees everything they posted. */
    postedSales: SaleType[] = []
    postedAccommodations: AccommodationType[] = []

    trackById = (_index: number, item: SaleType | AccommodationType) => `${item._type}_${item.id}`

    /** `sale_12` / `accommodation_5` - the posted_items key, and what a Manage link sends as `?post=`. */
    postKey = (item: SaleType | AccommodationType) => `${item._type}_${item.id}`

    /** 0 Saved, 1 Post history. */
    selectedTab = 0

    /** From a Manage link (`?post=sale_12`): scrolled to and briefly highlighted once it's on the page. */
    private postToReveal: string | null = null
    highlightedPost: string | null = null

    private destroy$ = new Subject<void>()
    private revealTimer?: ReturnType<typeof setTimeout>

    dev = isDevMode()

    theAgent: any = {}

    houses: Array<any> = [] // get data type of houses
    houseTypes: Array<string> = [
        'Duplex',
        'Bongalow',
        'Flat',
        'Skyscrapper',
        'Dungeon',
        'Castle',
    ]

    constructor(
        private callerService: CallerService,
        private breakpointObserver: BreakpointObserver,
        public dialog: MatDialog,
        private formBuilder: FormBuilder,
        public store: Store<AppState>,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        // but this whole component needs updating. We can get the ~agent~ user from the store.
        const agnt = localStorage.getItem(CallerService.LOCAL_STORAGE_DATA_KEY)
        if (agnt) {
            this.theAgent = JSON.parse(agnt) // TODO: we aren't using agent. shd be user.
        }
    }

    propertyForm = new FormGroup(
        {
            by: new FormControl(), // JSON.parse(this.theAgent).resource_uri
            bedrooms: new FormControl(''),
            kitchen: new FormControl(''),
            toilet: new FormControl(''),
            bathrooms: new FormControl(''),
            // graphics: new FormControl('', [Validators.required]),
            house_type: new FormControl('', [Validators.required]),
            price: new FormControl('', [Validators.required]),
            street: new FormControl(''),
            city_or_town: new FormControl(''),
            state: new FormControl(''),
            number: new FormControl(''),
        },
        [Validators.required],
    )

    houseFormData = new FormData()

    step = 0

    loadingHouseDataTries = 0

    folders: any[] = [
        {
            name: 'Photos',
            updated: new Date('1/1/16'),
        },
        {
            name: 'Recipes',
            updated: new Date('1/17/16'),
        },
        {
            name: 'Work',
            updated: new Date('1/28/16'),
        },
    ]
    notes: any[] = [
        {
            name: 'Vacation Itinerary',
            updated: new Date('2/20/16'),
        },
        {
            name: 'Kitchen Remodel',
            updated: new Date('1/18/16'),
        },
    ]

    setStep(index: number) {
        this.step = index
    }

    nextStep() {
        this.step++
    }

    prevStep() {
        this.step--
    }

    ngOnInit() {
        this.postToReveal = this.route.snapshot.queryParamMap.get('post')
        if (this.postToReveal) {
            this.selectedTab = 1
            // Drop the param, so a reload or Back doesn't highlight it again.
            this.router.navigate([], { queryParams: { post: null }, queryParamsHandling: 'merge', replaceUrl: true })
        }

        this.store.select('posted_items').pipe(takeUntil(this.destroy$)).subscribe({
            next: (value) => {
                this.allPosts = value
                const posts = [...value.values()]
                this.postedSales = posts.filter((post): post is SaleType => post._type === 'sale')
                this.postedAccommodations = posts.filter((post): post is AccommodationType => post._type === 'accommodation')
                if (this.postToReveal && value.has(this.postToReveal)) {
                    const key = this.postToReveal
                    this.postToReveal = null
                    this.revealPost(key)
                }
            }
        })

        this.loadHousesData()
        this.fetchAllBookmarks()
        this.fetchAllPosts()
    }

    fetchAllBookmarks() {
      this.callerService.getAllBookmarkedItems().subscribe({
          next: (res: HttpResponse<any>) => {
              console.log('bookmarked data', res)
              if (res.status === HttpStatusCode.Ok) {
                  this.allBookmarks = res.body.data
              } // TODO: need else block?
          },
          error: (err) => {
              this.callerService.showNotification('Failed to retrieve bookmarks')
          },
      })
    }

    // @todo: this should be in state. so when we delete, we can easily update state.
    fetchAllPosts() {
      this.callerService.getAllPostedItems().subscribe({
          next: (res: HttpResponse<any>) => {
              console.log('all posted data', res.body.data)
              
              if (res.status === HttpStatusCode.Ok) {
                const d = res.body.data.map((d: any) => [`${d._type}_${d.id}`, d])
              
                this.store.dispatch(initializePostsHistory({ data: new Map([...d]) }))

                // A Manage link to a post that isn't here (deleted since, say) - the store subscription found nothing to reveal.
                if (this.postToReveal) {
                    this.postToReveal = null
                    this.callerService.showNotification("Couldn't find that post. It may have been deleted.")
                }

                //   this.allPosts = res.body.data
              } // TODO: need else block?
          },
          error: (err) => {
              this.postToReveal = null
              this.callerService.showNotification('Failed to retrieve your posts')
          },
      })
    }

    ngOnDestroy() {
        this.destroy$.next()
        this.destroy$.complete()
        clearTimeout(this.revealTimer)
    }

    /** Scrolls to a posted item and highlights it for a moment, like a replied-to message in chat. Waits (a little) for the card to render. */
    private revealPost(key: string, attemptsLeft = 20) {
        this.revealTimer = setTimeout(() => {
            const card = document.getElementById('post-' + key)
            if (!card) {
                if (attemptsLeft > 1) this.revealPost(key, attemptsLeft - 1)
                return
            }
            card.scrollIntoView({ behavior: 'smooth', block: 'center' })
            this.highlightedPost = key
            this.revealTimer = setTimeout(() => (this.highlightedPost = null), 2000)
        }, 100)
    }

    loadHousesData() {}

    aHouseImages(): void {}

    deleteHouseImage(): void {}

    houseMedia(event: any, houseID: number): void {
        console.log('house media', event.target.files)
    }

    updateHouseInfo(): void {}

    postHouse() {
        console.log('posting', this.propertyForm.value)

        // convert formControl to formData
        /* Object.entries(this.propertyForm.value).forEach((value: [string, string | Blob], index, arr) => {
      this.houseFormData.append(value[0], value[1]);
    }); */
    }

    canDeactivate(): Promise<any> | boolean {
        if (this.houseFormData.has('images')) {
            // means there's a picture to post/update
            return new Promise((resolve, reject) => {
                const confirm = this.dialog.open(
                    ExitConfirmationDialogComponent,
                    {
                        data: {
                            message: 'unsaved changes',
                            username: 'u-name',
                        },
                    },
                )
                confirm.afterClosed().subscribe(
                    (res) => {
                        if (res === 'wait') {
                            resolve(false)
                        } else {
                            resolve(true)
                        }
                    },
                    (err) => {
                        // might never get here, to err
                        console.log(`exit confirmation Dialog error: ${err}`)
                        resolve(false)
                    },
                )
            })
        } else {
            return true
        }
    }
}
